import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Snackbar,
  Button,
  Box,
  Divider,
  Chip,
  Paper,
  Avatar,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Error as ErrorIcon, 
  AttachMoney, 
  TrendingUp,
  TrendingDown,
  Today,
  CalendarToday,
  ShowChart,
  Refresh,
  MonetizationOn,
  Receipt,
  MoneyOff,
  LocalAtm
} from '@material-ui/icons';
import CountUp from 'react-countup';
import ProduitVenduService from '../../services/ProduitVenduService';
import FondService from '../../services/FondService';
import { useSelector } from 'react-redux';

// =============================================
// CONSTANTS & CONFIGURATION
// =============================================

// Feature flag pour la TVA
const FEATURE_TVA_ENABLED = process.env.REACT_APP_FEATURE_FLIPPING_TVA === 'true';

const INITIAL_STATS = {
  stats_par_mode: [],
  totaux_globaux: { 
    total_ht: 0, 
    total_tva: 0, 
    total_ttc: 0, 
    total_ventes: 0 
  }
};

const INITIAL_DEPENSES = {
  aujourdhui: 0,
  hier: 0,
  mois_courant: 0,
  annee_courante: 0
};

const MODE_CONFIG = {
  ESPECE: { icon: AttachMoney, color: '#1c5853' },
  MOBILE: { icon: Receipt, color: '#2196F3' },
  OM: { icon: MonetizationOn, color: '#FF9800' },
  WAVE: { icon: MonetizationOn, color: '#4f95db' },
  DEFAULT: { icon: AttachMoney, color: '#757575' }
};

const PERIODE_ICONS = {
  aujourdhui: Today,
  hier: CalendarToday,
  mois_courant: ShowChart,
  annee_courante: TrendingUp
};

// =============================================
// STYLES
// =============================================

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fafafa',
    minHeight: '100vh',
    padding: theme.spacing(3)
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  headerTitle: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1.8rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  headerSubtitle: {
    color: '#666',
    fontSize: '1rem'
  },
  headerFeatureFlag: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    backgroundColor: FEATURE_TVA_ENABLED ? '#4caf50' : '#ff9800',
    color: 'white',
    borderRadius: 4,
    fontSize: '0.7rem',
    fontWeight: 600
  },
  periodCard: {
    height: '100%',
    borderRadius: 12,
    backgroundColor: 'white',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
    }
  },
  periodHeader: {
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #eaeaea',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12
  },
  periodTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#444',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  periodIcon: {
    color: '#1c5853',
    backgroundColor: '#E8F5E9',
    borderRadius: '50%',
    padding: theme.spacing(0.75),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  periodChip: {
    backgroundColor: '#1c5853',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.8rem',
    marginLeft: 'auto'
  },
  modeItem: {
    padding: theme.spacing(2),
    margin: theme.spacing(1.5, 0),
    backgroundColor: 'white',
    borderRadius: 10,
    border: '1px solid #e0e0e0'
  },
  modeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5)
  },
  modeNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  modeChip: {
    backgroundColor: '#2196F3',
    color: 'white',
    fontWeight: 500,
    fontSize: '0.8rem'
  },
  montantVerticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1)
  },
  montantVerticalItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    borderRadius: 8
  },
  montantVerticalLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  totalsSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: '#f8f9fa',
    borderRadius: 10,
    border: '1px solid #e0e0e0'
  },
  totalsTitle: {
    fontWeight: 600,
    color: '#444',
    fontSize: '1rem',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  totalsVerticalGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  },
  totalVerticalItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: 'white',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
  },
  totalLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#666'
  },
  financeVerticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2.5)
  },
  financeVerticalItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderTop: '4px solid',
    minHeight: 120,
    justifyContent: 'space-between'
  },
  depenseVertical: {
    borderTopColor: '#F44336'
  },
  netVertical: {
    borderTopColor: '#1c5853'
  },
  financeVerticalTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#444',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  financeVerticalValueContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1
  },
  financeVerticalValue: {
    fontWeight: 700,
    fontSize: '1.3rem',
    textAlign: 'center',
    marginBottom: theme.spacing(0.5)
  },
  financeVerticalStatus: {
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'center',
    marginTop: theme.spacing(0.5)
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    gap: theme.spacing(2)
  },
  loadingText: {
    color: '#666',
    fontWeight: 500
  },
  errorContainer: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    backgroundColor: '#FFEBEE',
    borderRadius: 8,
    border: '1px solid #FFCDD2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  errorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  errorIcon: {
    color: '#F44336'
  },
  errorText: {
    fontWeight: 500,
    color: '#444'
  },
  retryButton: {
    backgroundColor: '#1c5853',
    color: 'white',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    padding: theme.spacing(0.5, 1.5),
    '&:hover': {
      backgroundColor: '#45a049'
    }
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#888'
  },
  emptyIcon: {
    fontSize: 48,
    color: '#BDBDBD',
    marginBottom: theme.spacing(2)
  },
  featureFlagBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 12,
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: FEATURE_TVA_ENABLED ? '#e8f5e9' : '#fff3e0',
    color: FEATURE_TVA_ENABLED ? '#2e7d32' : '#f57c00',
    border: `1px solid ${FEATURE_TVA_ENABLED ? '#c8e6c9' : '#ffe0b2'}`
  }
}));

// =============================================
// COMPONENTS
// =============================================

const FormattedMontant = ({ montant = 0, variant = 'body2', color = 'inherit', bold = true }) => {
  return (
    <Typography variant={variant} style={{ color, fontWeight: bold ? 700 : 400 }}>
      <CountUp 
        start={0} 
        end={montant} 
        duration={1.5} 
        separator=" " 
        decimals={2}
        decimal=","
        suffix=" F CFA"
      />
    </Typography>
  );
};

const LoadingState = ({ classes }) => (
  <Box className={classes.loadingContainer}>
    <CircularProgress size={50} style={{ color: '#1c5853' }} />
    <Typography className={classes.loadingText}>
      Chargement des données...
    </Typography>
  </Box>
);

const ErrorState = ({ error, onRetry, classes }) => (
  <Paper className={classes.errorContainer} elevation={0}>
    <Box className={classes.errorContent}>
      <ErrorIcon className={classes.errorIcon} />
      <Typography className={classes.errorText}>
        {error}
      </Typography>
    </Box>
    <Button 
      className={classes.retryButton}
      onClick={onRetry}
      startIcon={<Refresh />}
    >
      Réessayer
    </Button>
  </Paper>
);

const EmptyState = ({ classes }) => (
  <Box className={classes.emptyState}>
    <Today className={classes.emptyIcon} />
    <Typography variant="body1" style={{ color: '#888', fontWeight: 500 }}>
      Aucune vente enregistrée
    </Typography>
  </Box>
);

// =============================================
// MAIN COMPONENT
// =============================================

function StatsModePaiement() {
  const classes = useStyles();
  const theme = useTheme();
  const user = useSelector((state) => state.auth);
  const userRole = user?.rol;

  const [stats, setStats] = useState({
    aujourdhui: INITIAL_STATS,
    hier: INITIAL_STATS,
    mois_courant: INITIAL_STATS,
    annee_courante: INITIAL_STATS
  });
  
  const [depenses, setDepenses] = useState(INITIAL_DEPENSES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // =============================================
  // UTILITY FUNCTIONS
  // =============================================



  // Fonction pour obtenir le montant à afficher
// Fonction pour obtenir le montant à afficher
const getMontantDisplay = useCallback((item) => {
  if (FEATURE_TVA_ENABLED) {
    return parseFloat(item.montant_ttc) || 0;
  } else {
    return parseFloat(item.montant_ht) || 0;
  }
}, [FEATURE_TVA_ENABLED]);

  // Fonction pour obtenir le total à afficher
  const getTotalDisplay = useCallback((totaux) => {
    if (FEATURE_TVA_ENABLED) {
      return parseFloat(totaux.total_ttc) || 0;
    } else {
      return parseFloat(totaux.total_ht) || 0;
    }
  }, [FEATURE_TVA_ENABLED]);

// Fonction pour obtenir le montant HT réel (pour les calculs)
const getMontantHT = useCallback((item) => {
  return parseFloat(item.montant_ht) || 0;
}, []);

// Fonction pour obtenir le total HT réel (pour les calculs)
const getTotalHT = useCallback((totaux) => {
  return parseFloat(totaux.total_ht) || 0;
}, []);

  const getModeConfig = useCallback((mode) => {
    const modeUpper = mode?.toUpperCase();
    return MODE_CONFIG[modeUpper] || MODE_CONFIG.DEFAULT;
  }, []);

  const getPeriodeIcon = useCallback((key) => {
    const Icon = PERIODE_ICONS[key] || Today;
    return <Icon />;
  }, []);

  const extractMontantFromData = useCallback((data) => {
    if (!data) return 0;
    
    // Priorité: total -> montant_total -> somme d'array
    if (data.total !== undefined && data.total !== null) {
      return parseFloat(data.total) || 0;
    }
    
    if (data.montant_total !== undefined && data.montant_total !== null) {
      return parseFloat(data.montant_total) || 0;
    }
    
    if (Array.isArray(data)) {
      return data.reduce((sum, item) => sum + (parseFloat(item.montant) || 0), 0);
    }
    
    return 0;
  }, []);

// Fonction corrigée pour normaliser les données
const normalizeStatsData = useCallback((periodeData) => {
  if (!periodeData) return INITIAL_STATS;
  
  // Si c'est déjà un objet formaté, le retourner tel quel
  if (periodeData.stats_par_mode !== undefined) {
    return periodeData;
  }
  
  // Sinon, retourner une structure vide
  return INITIAL_STATS;
}, []);

  // =============================================
  // MEMOIZED VALUES
  // =============================================

  const periodes = useMemo(() => {
    const basePeriodes = [
      { key: 'aujourdhui', title: "Aujourd'hui" },
      { key: 'hier', title: 'Hier' }
    ];
    
    if (userRole === 'admin' || userRole === 'compta') {
      basePeriodes.push(
        { key: 'mois_courant', title: 'Ce mois' },
        { key: 'annee_courante', title: 'Cette année' }
      );
    }
    
    return basePeriodes;
  }, [userRole]);

  // =============================================
  // API FUNCTIONS
  // =============================================

  const fetchDepenses = useCallback(async () => {
    try {
      const endpoints = [
        { key: 'aujourdhui', service: FondService.depensesJournee },
        { key: 'hier', service: FondService.depensesHier },
        ...(userRole === 'admin' ? [
          { key: 'mois_courant', service: FondService.depensesMonth },
          { key: 'annee_courante', service: FondService.depensesYear }
        ] : [])
      ];

      const promises = endpoints.map(endpoint => endpoint.service());
      const results = await Promise.all(promises);

      const newDepenses = { ...INITIAL_DEPENSES };
      
      results.forEach((result, index) => {
        const key = endpoints[index].key;
        newDepenses[key] = extractMontantFromData(result.data);
      });

      setDepenses(newDepenses);
    } catch (error) {
      console.error('Erreur lors du chargement des dépenses:', error);
      setDepenses(INITIAL_DEPENSES);
    }
  }, [userRole, extractMontantFromData]);

  // Remplacer getMontantsFromAPI par :
const getMontantsFromAPI = useCallback((item) => {
  return {
    ht: parseFloat(item.montant_ht) || 0,
    tva: parseFloat(item.montant_tva) || 0,
    ttc: parseFloat(item.montant_ttc) || 0,
    taux: parseFloat(item.tva_taux) * 100 || 0  
  };
}, []);

// Ajoutez un logging amélioré pour le debug
const fetchData = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    
    const response = await ProduitVenduService.getStatsSynthese();
    console.log('📊 Données API brutes:', response.data);
    
    // Log détaillé pour chaque période
    Object.keys(response.data).forEach(periode => {
      const data = response.data[periode];
      console.log(`📊 Période ${periode}:`, data);
      if (Array.isArray(data)) {
        data.forEach((item, index) => {
          console.log(`📊   Item ${index}:`, {
            mode: item.mode_payement,
            ht: item.montant_ht,
            tva: item.montant_tva,
            ttc: item.montant_ttc,
            taux: item.tva_taux,
            ventes: item.nombre_ventes
          });
        });
      }
    });
    
    const normalizedStats = {};
    periodes.forEach(periode => {
      normalizedStats[periode.key] = normalizeStatsData(response.data[periode.key]);
    });
    
    console.log('📊 Données normalisées:', normalizedStats);
    setStats(normalizedStats);
    await fetchDepenses();
    
  } catch (error) {
    console.error('Erreur lors du chargement des stats:', error);
    setError('Impossible de charger les statistiques');
    setSnackbarOpen(true);
  } finally {
    setLoading(false);
  }
}, [periodes, normalizeStatsData, fetchDepenses]);
  // =============================================
  // EFFECTS
  // =============================================

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // =============================================
  // EVENT HANDLERS
  // =============================================

  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // =============================================
  // RENDER FUNCTIONS
  // =============================================

// Mettez à jour la fonction renderMontantItems :
const renderMontantItems = useCallback((item) => {
  const montants = getMontantsFromAPI(item);
  const tvaTaux = parseFloat(item.tva_taux) || 0;
  
  if (FEATURE_TVA_ENABLED) {
    return (
      <>
        <Box className={classes.montantVerticalItem}>
          <Typography className={classes.montantVerticalLabel}>HT</Typography>
          <FormattedMontant 
            montant={montants.ht}
            variant="body2"
            color="#1976d2"
          />
        </Box>
        
        <Box className={classes.montantVerticalItem}>
          <Typography className={classes.montantVerticalLabel}>
            TVA ({tvaTaux * 100}%)
          </Typography>
          <FormattedMontant 
            montant={montants.tva}
            variant="body2"
            color="#2e7d32"
          />
        </Box>
        
        <Box className={classes.montantVerticalItem}>
          <Typography className={classes.montantVerticalLabel}>TTC</Typography>
          <FormattedMontant 
            montant={montants.ttc}
            variant="body2"
            color="#ef6c00"
          />
        </Box>
      </>
    );
  } else {
    return (
      <Box className={classes.montantVerticalItem}>
        <Typography className={classes.montantVerticalLabel}>MONTANT</Typography>
        <FormattedMontant 
          montant={montants.ht}
          variant="body2"
          color="#1c5853"
        />
      </Box>
    );
  }
}, [classes, FEATURE_TVA_ENABLED, getMontantsFromAPI]);


  const renderModeItem = useCallback((item, index) => {
    const config = getModeConfig(item.mode_payement);
    const Icon = config.icon;
    
    return (
      <Paper key={index} className={classes.modeItem} elevation={0}>
        <Box className={classes.modeHeader}>
          <Box className={classes.modeNameContainer}>
            <Avatar style={{ backgroundColor: config.color, width: 40, height: 40 }}>
              <Icon />
            </Avatar>
            <Typography className={classes.modeName}>
              {item.mode_payement || 'Autre'}
            </Typography>
          </Box>
          <Chip 
            size="small"
            label={`${item.nombre_ventes || 0} vente${item.nombre_ventes > 1 ? 's' : ''}`}
            className={classes.modeChip}
            style={{ backgroundColor: config.color }}
          />
        </Box>

        <Box className={classes.montantVerticalContainer}>
          {renderMontantItems(item)}
        </Box>
      </Paper>
    );
  }, [classes, getModeConfig, renderMontantItems]);

// Mettez à jour la fonction renderTotalItems :
const renderTotalItems = useCallback((totaux) => {
  if (FEATURE_TVA_ENABLED) {
    return (
      <>
        <Box className={classes.totalVerticalItem}>
          <Typography className={classes.totalLabel}>Total HT</Typography>
          <FormattedMontant 
            montant={parseFloat(totaux.total_ht) || 0}
            variant="body1"
            color="#1976d2"
          />
        </Box>
        
        <Box className={classes.totalVerticalItem}>
          <Typography className={classes.totalLabel}>Total TVA</Typography>
          <FormattedMontant 
            montant={parseFloat(totaux.total_tva) || 0}
            variant="body1"
            color="#2e7d32"
          />
        </Box>
        
        <Box className={classes.totalVerticalItem}>
          <Typography className={classes.totalLabel}>Total TTC</Typography>
          <FormattedMontant 
            montant={parseFloat(totaux.total_ttc) || 0}
            variant="h6"
            color="#ef6c00"
          />
        </Box>
      </>
    );
  } else {
    return (
      <Box className={classes.totalVerticalItem}>
        <Typography className={classes.totalLabel}>Total Ventes</Typography>
        <FormattedMontant 
          montant={parseFloat(totaux.total_ht) || 0}
          variant="h6"
          color="#1c5853"
        />
      </Box>
    );
  }
}, [FEATURE_TVA_ENABLED, classes]);


  const renderPeriodeCard = useCallback((periode) => {
    const periodeData = stats[periode.key] || INITIAL_STATS;
    const statsParMode = periodeData.stats_par_mode || [];
    const totaux = periodeData.totaux_globaux || {};
    const totalDepenses = depenses[periode.key] || 0;
    
    // Utiliser getTotalHT pour les calculs (cohérence avec l'autre composant)
    const totalVentesHT = getTotalHT(totaux);
    
    // Afficher le total selon le feature flag
    const totalDisplay = getTotalDisplay(totaux);
    
    // Calculer le net en utilisant le HT pour les calculs
    const net = totalVentesHT - totalDepenses;
    
    console.log(`Rendu ${periode.key}:`, {
      totaux,
      totalDepenses,
      totalVentesHT,
      net
    });
    
    return (
      <Grid item xs={12} md={6} lg={3} key={periode.key}>
        <Card className={classes.periodCard} elevation={0}>
          <CardContent style={{ padding: 0 }}>
            <Box className={classes.periodHeader}>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography className={classes.periodTitle}>
                  <Box className={classes.periodIcon}>
                    {getPeriodeIcon(periode.key)}
                  </Box>
                  {periode.title}
                </Typography>
                <Chip 
                  label={`${totaux.total_ventes || 0} ventes`}
                  size="small"
                  className={classes.periodChip}
                />
              </Box>
            </Box>

            <Box style={{ padding: theme.spacing(2) }}>
              {statsParMode.length > 0 ? (
                <>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#444', marginBottom: theme.spacing(1.5) }}>
                    Ventilation par mode de paiement
                  </Typography>
                  
                  {statsParMode.map(renderModeItem)}

                  <Box className={classes.totalsSection}>
                    <Typography className={classes.totalsTitle}>
                      <TrendingUp style={{ fontSize: 18 }} />
                      Totaux de la période
                    </Typography>
                    <Box className={classes.totalsVerticalGrid}>
                      {renderTotalItems(totaux)}
                    </Box>
                  </Box>

                  <Box className={classes.financeVerticalContainer}>
                    <Paper className={`${classes.financeVerticalItem} ${classes.depenseVertical}`}>
                      <Typography className={classes.financeVerticalTitle}>
                        <TrendingDown fontSize="small" />
                        Dépenses
                      </Typography>
                      <Box className={classes.financeVerticalValueContainer}>
                        <FormattedMontant 
                          montant={totalDepenses}
                          variant="h5"
                          color="#F44336"
                        />
                      </Box>
                    </Paper>

                    <Paper className={`${classes.financeVerticalItem} ${classes.netVertical}`}>
                      <Typography className={classes.financeVerticalTitle}>
                        {net >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                        Résultat Net
                      </Typography>
                      <Box className={classes.financeVerticalValueContainer}>
                        <FormattedMontant 
                          montant={Math.abs(net)}
                          variant="h5"
                          color={net >= 0 ? '#1c5853' : '#F44336'}
                        />
                        <Typography 
                          className={classes.financeVerticalStatus}
                          style={{ color: net >= 0 ? '#1c5853' : '#F44336' }}
                        >
                          {net >= 0 ? '✓ Bénéfice' : '✗ Perte'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                </>
              ) : (
                <EmptyState classes={classes} />
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }, [classes, stats, depenses, theme, renderModeItem, getPeriodeIcon, renderTotalItems, getTotalDisplay, getTotalHT]);

  const renderResumeComparatif = useCallback(() => {
    if (periodes.length <= 1) return null;

    return (
      <Paper elevation={0} style={{ marginTop: theme.spacing(4), padding: theme.spacing(3), backgroundColor: 'white', borderRadius: 12 }}>
        <Typography variant="h6" style={{ fontWeight: 600, color: '#333', marginBottom: theme.spacing(3) }}>
          <ShowChart style={{ marginRight: theme.spacing(1), verticalAlign: 'middle' }} />
          Résumé Comparatif
        </Typography>
        <Grid container spacing={2}>
          {periodes.map((periode) => {
            const periodeData = stats[periode.key] || { totaux_globaux: {} };
            const totaux = periodeData.totaux_globaux || {};
            const totalDepenses = depenses[periode.key] || 0;
            const totalVentesHT = getTotalHT(totaux);
            const totalDisplay = getTotalDisplay(totaux);
            const net = totalVentesHT - totalDepenses;
            
            return (
              <Grid item xs={12} sm={6} md={3} key={periode.key}>
                <Box style={{ padding: theme.spacing(2), backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#444', marginBottom: theme.spacing(1.5) }}>
                    {periode.title}
                  </Typography>
                  
                  {FEATURE_TVA_ENABLED ? (
                    <>
                      <Box style={{ marginBottom: theme.spacing(1) }}>
                        <Typography variant="caption" style={{ color: '#666' }}>Total HT</Typography>
                        <FormattedMontant 
                          montant={parseFloat(totaux.total_ht) || 0}
                          variant="body2"
                          color="#1976d2"
                        />
                      </Box>
                      
                      <Box style={{ marginBottom: theme.spacing(1) }}>
                        <Typography variant="caption" style={{ color: '#666' }}>Total TVA</Typography>
                        <FormattedMontant 
                          montant={parseFloat(totaux.total_tva) || 0}
                          variant="body2"
                          color="#2e7d32"
                        />
                      </Box>
                      
                      <Box style={{ marginBottom: theme.spacing(1) }}>
                        <Typography variant="caption" style={{ color: '#666' }}>Total TTC</Typography>
                        <FormattedMontant 
                          montant={parseFloat(totaux.total_ttc) || 0}
                          variant="body1"
                          color="#ef6c00"
                        />
                      </Box>
                    </>
                  ) : (
                    <Box style={{ marginBottom: theme.spacing(1) }}>
                      <Typography variant="caption" style={{ color: '#666' }}>Total Ventes</Typography>
                      <FormattedMontant 
                        montant={totalDisplay}
                        variant="body1"
                        color="#1c5853"
                      />
                    </Box>
                  )}
                  
                  <Divider style={{ margin: theme.spacing(1.5, 0) }} />
                  
                  <Box style={{ marginTop: theme.spacing(1), paddingTop: theme.spacing(1.5) }}>
                    <Box style={{ marginBottom: theme.spacing(1.5), padding: theme.spacing(1), backgroundColor: '#fff5f5', borderRadius: 6 }}>
                      <Typography variant="caption" style={{ color: '#F44336', fontWeight: 600, marginBottom: theme.spacing(0.5) }}>
                        Dépenses
                      </Typography>
                      <FormattedMontant 
                        montant={totalDepenses}
                        variant="body2"
                        color="#F44336"
                      />
                    </Box>
                    
                    <Box style={{ padding: theme.spacing(1), backgroundColor: net >= 0 ? '#f0f9f0' : '#fff5f5', borderRadius: 6 }}>
                      <Typography variant="caption" style={{ color: net >= 0 ? '#1c5853' : '#F44336', fontWeight: 600, marginBottom: theme.spacing(0.5) }}>
                        Résultat Net
                      </Typography>
                      <FormattedMontant 
                        montant={Math.abs(net)}
                        variant="body1"
                        color={net >= 0 ? '#1c5853' : '#F44336'}
                      />
                      <Typography variant="caption" style={{ color: net >= 0 ? '#1c5853' : '#F44336', fontWeight: 600, marginTop: theme.spacing(0.25) }}>
                        {net >= 0 ? '✓ Bénéfice' : '✗ Perte'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" style={{ display: 'block', color: '#888', marginTop: theme.spacing(1.5), textAlign: 'center' }}>
                    {totaux.total_ventes || 0} transaction(s)
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  }, [periodes, stats, depenses, theme, getTotalDisplay, getTotalHT]);

  // =============================================
  // MAIN RENDER
  // =============================================

  if (loading) {
    return <LoadingState classes={classes} />;
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.headerTitle}>
            <MonetizationOn style={{ fontSize: 28 }} />
            Tableau de Bord Financier
            <span className={classes.featureFlagBadge}>
              {FEATURE_TVA_ENABLED ? (
                <>
                  <LocalAtm fontSize="small" />
                  Avec TVA
                </>
              ) : (
                <>
                  <MoneyOff fontSize="small" />
                  Sans TVA
                </>
              )}
            </span>
          </Typography>
          <Typography className={classes.headerSubtitle}>
            Analyse des ventes par mode de paiement
            {FEATURE_TVA_ENABLED ? ' (TVA incluse)' : ' (TVA non applicable)'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Refresh />}
          onClick={handleRetry}
          style={{ backgroundColor: '#1c5853' }}
        >
          Actualiser
        </Button>
      </Box>

      {error && (
        <ErrorState 
          error={error} 
          onRetry={handleRetry} 
          classes={classes} 
        />
      )}

      {/* Cartes des périodes */}
      <Grid container spacing={3}>
        {periodes.map(renderPeriodeCard)}
      </Grid>

      {/* Résumé comparatif */}
      {renderResumeComparatif()}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={error}
        action={
          <Button color="inherit" size="small" onClick={handleSnackbarClose}>
            Fermer
          </Button>
        }
      />
    </Box>
  );
}

export default StatsModePaiement;